import React, {useState, useEffect, useMemo} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { customerSlice } from "./../../store";
import {
  AuthService,
  CustomerService,
  EventsService,
  formatCustomerDisplayName,
  compareCustomersByNameColumn,
} from "../../services";
import {
  CUSTOMER_TYPE,
  CUSTOMER_TYPE_TEXT,
  isCustomerListActive,
  PROGRAM_TYPE,
  PROGRAM_TYPE_TEXT,
  PAY_SOURCE,
  PAY_SOURCE_TEXT,
  LEGAL_SEX_TEXT,
  MARITAL_STATUS_TEXT,
  IMMIGRATION_STATUS_TEXT,
  LANGUAGE_OPTIONS,
  DAYS_OF_WEEK_OPTIONS,
  REFERRAL_SOURCE_TEXT,
  CUSTOMER_DISCHARGE_REASON_TEXT,
  DIETARY_RESTRICTIONS_OPTIONS,
  DIET_TEXTURE_TEXT,
  MEAL_TYPE_TEXT,
  TRANSPORTATION_TYPE,
  TRANSPORTATION_TYPE_TEXT,
  PREFERRED_TEXT_LANGUAGE_TEXT,
  YES_NO,
  YES_NO_TEXT,
  ManageTable,
  CustomerAvatar,
  useCustomerAvatarUrl,
} from "../../shared";
import { readPageUiObject, writePageUiObject } from "../../shared/pageFilterPersistence";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Dropdown, Modal } from "react-bootstrap";
import { Columns, Download, Filter, PencilSquare, Plus } from "react-bootstrap-icons";
import moment from "moment";

/** Customer list columns that represent dates — sort chronologically; empty = minimum (first on asc, last on desc). */
const CUSTOMER_DATE_SORT_COLUMN_KEYS = new Set([
  "date_of_birth",
  "marriage_date",
  "admission_date",
  "enrolled_date",
  "discharge_date",
  "adcaps_completed_date",
  "medicaid_renew_date",
  "id_expiration_date"
]);

const EMPTY_DATE_SORT_MS = Number.NEGATIVE_INFINITY;

const getCustomerListDateSortMs = (value) => {
  if (value === undefined || value === null) return EMPTY_DATE_SORT_MS;
  const s = `${value}`.trim();
  if (!s) return EMPTY_DATE_SORT_MS;
  const strict = moment(s, ["MM/DD/YYYY", "M/D/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
  if (strict.isValid()) return strict.valueOf();
  const loose = moment(s);
  return loose.isValid() ? loose.valueOf() : EMPTY_DATE_SORT_MS;
};

/** No column sort: members (and self_pay) before visitors, then last name, then first name. */
const customerListDefaultTypeRank = (type) => {
  if (type === CUSTOMER_TYPE.MEMBER || type === CUSTOMER_TYPE.SELF_PAY) return 0;
  if (type === CUSTOMER_TYPE.VISITOR) return 1;
  return 2;
};

const compareCustomersListDefaultOrder = (a, b) => {
  const ra = customerListDefaultTypeRank(a?.type);
  const rb = customerListDefaultTypeRank(b?.type);
  if (ra !== rb) return ra - rb;
  const la = `${a?.lastname ?? ''}`.toLowerCase();
  const lb = `${b?.lastname ?? ''}`.toLowerCase();
  const byLast = la.localeCompare(lb, undefined, { sensitivity: 'base', numeric: true });
  if (byLast !== 0) return byLast;
  const fa = `${a?.firstname ?? ''}`.toLowerCase();
  const fb = `${b?.firstname ?? ''}`.toLowerCase();
  return fa.localeCompare(fb, undefined, { sensitivity: 'base', numeric: true });
};

const DashboardCustomersList = ({
  additionalButtons,
  showBreadcrumb = false,
  title = null,
  /** When true (e.g. main Dashboard), table header stays visible while the list scrolls vertically. */
  freezeTableHeader = false,
  enableBulkSelect = false,
}) => {
  const HIDDEN_CUSTOMER_TYPE_FILTER_VALUES = [CUSTOMER_TYPE.SELF_PAY, CUSTOMER_TYPE.TRANSFERRED, CUSTOMER_TYPE.DECEASED, CUSTOMER_TYPE.DISCHARGED];
  const DISCHARGE_ONLY_COLUMN_KEYS = new Set(['discharge_date', 'discharge_by', 'discharge_reason']);
  const CUSTOMER_COLUMNS = [
    // Basic Info
    { key: 'customer_name', label: 'Customer Name', show: true, section: 'Basic Info' },
    { key: 'customer_type', label: 'Customer Type', show: true, section: 'Basic Info' },
    { key: 'program_type_display', label: 'Program Type', show: true, section: 'Basic Info' },
    { key: 'pay_source_display', label: 'Pay Source', show: true, section: 'Basic Info' },
    { key: 'date_of_birth', label: 'Date Of Birth', show: true, section: 'Basic Info' },
    { key: 'legal_sex_display', label: 'Legal Sex', show: true, section: 'Basic Info' },
    { key: 'marital_status_display', label: 'Marital Status', show: true, section: 'Basic Info' },
    { key: 'marriage_date', label: 'Marriage Date', show: true, section: 'Basic Info' },
    { key: 'immigration_status_display', label: 'Immigration Status', show: true, section: 'Basic Info' },
    { key: 'language_spoken_display', label: 'Language Spoken', show: true, section: 'Basic Info' },
    // Contact Info
    { key: 'phone_number', label: 'Phone Number', show: true, section: 'Contact Info' },
    { key: 'email', label: 'Email', show: true, section: 'Contact Info' },
    // Address
    { key: 'address_display', label: 'Address', show: true, section: 'Address' },
    // Emergency Contact
    { key: 'emergency_contact_display', label: 'Emergency Contact', show: true, section: 'Emergency Contact' },
    // Schedule
    { key: 'days_of_week_display', label: 'Days of Week', show: true, section: 'Schedule' },
    // Admission & Discharge Record
    { key: 'admission_date', label: 'Admission Date', show: true, section: 'Admission & Discharge Record' },
    { key: 'enrolled_date', label: 'Enrolled Date', show: true, section: 'Admission & Discharge Record' },
    { key: 'created_by', label: 'Created By', show: true, section: 'Admission & Discharge Record' },
    { key: 'referral_source_display', label: 'Referral Source', show: true, section: 'Admission & Discharge Record' },
    { key: 'discharge_date', label: 'Discharge Date', show: true, section: 'Admission & Discharge Record' },
    { key: 'discharge_by', label: 'Discharge By', show: true, section: 'Admission & Discharge Record' },
    { key: 'discharge_reason', label: 'Discharge Reason', show: true, section: 'Admission & Discharge Record' },
    // Additional Information
    { key: 'notes_and_attachments', label: 'Notes And Attachments', show: true, section: 'Additional Information' },
    { key: 'notes_for_driver', label: 'Notes for Driver', show: true, section: 'Additional Information' },
    // Care & Services
    { key: 'dietary_restrictions_display', label: 'Diet Preferences', show: true, section: 'Care & Services' },
    { key: 'diet_texture_display', label: 'Diet Texture', show: true, section: 'Care & Services' },
    { key: 'meal_type', label: 'Meal Type', show: true, section: 'Care & Services' },
    { key: 'table_number', label: 'Table Number', show: true, section: 'Care & Services' },
    { key: 'seat_number', label: 'Seat Number', show: true, section: 'Care & Services' },
    { key: 'transportation_type_display', label: 'Transportation Type', show: true, section: 'Care & Services' },
    { key: 'wheelchair_display', label: 'Lift-Equipped Vehicle Required', show: true, section: 'Care & Services' },
    { key: 'consent_to_text_messages_display', label: 'Consent To Text Messages', show: true, section: 'Care & Services' },
    { key: 'preferred_text_language_display', label: 'Preferred Text Language', show: true, section: 'Care & Services' },
    { key: 'consent_to_media_use_display', label: 'Consent to Media Use', show: true, section: 'Care & Services' },
    // Providers
    { key: 'primary_care_physician_display', label: 'Primary Care Physician', show: true, section: 'Providers' },
    { key: 'pharmacy_name', label: 'Pharmacy Name', show: true, section: 'Providers' },
    { key: 'pharmacy_id', label: 'Pharmacy ID', show: true, section: 'Providers' },
    // General Conditions
    { key: 'diabetes_mellitus_display', label: 'Diabetes Mellitus', show: true, section: 'General Conditions' },
    { key: 'eyes_on_display', label: 'Eyes-On', show: true, section: 'General Conditions' },
    { key: 'weight', label: 'Weight', show: true, section: 'General Conditions' },
    // Legal
    { key: 'molst', label: 'MOLST', show: true, section: 'Legal' },
    { key: 'provisions_for_advance_medical', label: 'Advance Directive /Living Will', show: true, section: 'Legal' },
    { key: 'hospice_display', label: 'Hospice', show: true, section: 'Legal' },
    { key: 'burial_arrangements', label: 'CPR', show: true, section: 'Legal' },
    { key: 'power_of_attorney', label: 'Power of Attorney', show: true, section: 'Legal' },
    // Medication Management
    { key: 'medications_given_at_center_display', label: 'Medications given at the center', show: true, section: 'Medication Management' },
    { key: 'pillbox_display', label: 'Pillbox', show: true, section: 'Medication Management' },
    { key: 'medication_note', label: 'Medication Note', show: true, section: 'Medication Management' },
    { key: 'medication_type', label: 'Medication Type', show: true, section: 'Medication Management' },
    { key: 'pillbox_type', label: 'Pillbox Type', show: true, section: 'Medication Management' },
    { key: 'pillbox_note', label: 'Pillbox Note', show: true, section: 'Medication Management' },
    // Rounding
    { key: 'requires_rounding_display', label: 'Requires Rounding', show: true, section: 'Rounding' },
    { key: 'rounding_notes', label: 'Rounding Notes', show: true, section: 'Rounding' },
    // Compliance & Deadlines
    { key: 'adcaps_completed_date', label: 'Adcaps Due Date', show: true, section: 'Compliance & Deadlines' },
    { key: 'adcaps_note', label: 'ADCAPS Note', show: true, section: 'Compliance & Deadlines' },
    { key: 'ltss_id_number', label: 'LTSS ID Number', show: true, section: 'Compliance & Deadlines' },
    { key: 'medicaid_renew_date', label: 'Medicaid Renew Date', show: true, section: 'Compliance & Deadlines' },
    { key: 'id_expiration_date', label: 'ID Expiration Date', show: true, section: 'Compliance & Deadlines' },
    // Confidential Details
    { key: 'medicare_number', label: 'Medicare Number', show: true, section: 'Confidential Details' },
    { key: 'medicaid_number', label: 'Medicaid Number', show: true, section: 'Confidential Details' },
    { key: 'social_security_number', label: 'Social Security Number', show: true, section: 'Confidential Details' },
  ];
  const CUSTOMER_LIST_COLUMN_TAB_MAP = {
    customer_name: 'personalInfo',
    customer_type: 'careServices',
    program_type_display: 'personalInfo',
    pay_source_display: 'personalInfo',
    date_of_birth: 'personalInfo',
    legal_sex_display: 'personalInfo',
    marital_status_display: 'personalInfo',
    marriage_date: 'personalInfo',
    immigration_status_display: 'personalInfo',
    language_spoken_display: 'personalInfo',
    phone_number: 'personalInfo',
    email: 'personalInfo',
    address_display: 'personalInfo',
    emergency_contact_display: 'personalInfo',
    admission_date: 'careServices',
    days_of_week_display: 'careServices',
    enrolled_date: 'careServices',
    created_by: 'careServices',
    referral_source_display: 'careServices',
    discharge_date: 'careServices',
    discharge_by: 'careServices',
    discharge_reason: 'careServices',
    notes_and_attachments: 'careServices',
    notes_for_driver: 'careServices',
    dietary_restrictions_display: 'careServices',
    diet_texture_display: 'careServices',
    meal_type: 'careServices',
    table_number: 'careServices',
    seat_number: 'careServices',
    transportation_type_display: 'careServices',
    wheelchair_display: 'careServices',
    consent_to_text_messages_display: 'careServices',
    preferred_text_language_display: 'careServices',
    consent_to_media_use_display: 'careServices',
    primary_care_physician_display: 'medicalInsurance',
    pharmacy_name: 'medicalInsurance',
    pharmacy_id: 'medicalInsurance',
    diabetes_mellitus_display: 'medicalInsurance',
    eyes_on_display: 'medicalInsurance',
    weight: 'medicalInsurance',
    molst: 'medicalInsurance',
    provisions_for_advance_medical: 'medicalInsurance',
    hospice_display: 'medicalInsurance',
    burial_arrangements: 'medicalInsurance',
    power_of_attorney: 'medicalInsurance',
    adcaps_note: 'medicalInsurance',
    medications_given_at_center_display: 'medicalInsurance',
    pillbox_display: 'medicalInsurance',
    medication_note: 'medicalInsurance',
    medication_type: 'medicalInsurance',
    pillbox_type: 'medicalInsurance',
    pillbox_note: 'medicalInsurance',
    requires_rounding_display: 'medicalInsurance',
    rounding_notes: 'medicalInsurance',
    adcaps_completed_date: 'medicalInsurance',
    ltss_id_number: 'medicalInsurance',
    medicaid_renew_date: 'confidentialDetails',
    id_expiration_date: 'confidentialDetails',
    medicare_number: 'confidentialDetails',
    medicaid_number: 'confidentialDetails',
    social_security_number: 'confidentialDetails',
  };
  const getVisibleColumnsByPermission = (columnList = []) => {
    return columnList.filter((column) => {
      if (column.key === 'customer_name') return true;
      const mappedTab = CUSTOMER_LIST_COLUMN_TAB_MAP[column.key];
      if (!mappedTab) return true;
      return AuthService.canViewCustomerTab(mappedTab);
    });
  };
  const CUSTOMER_FILTER_TAB_MAP = {
    customerType: 'careServices',
    programType: 'personalInfo',
    paySource: 'personalInfo',
    eyesOn: 'medicalInsurance',
    hasAirtag: 'medicalInsurance',
    transportationType: 'careServices',
    liftEquippedVehicle: 'careServices',
  };
  const canViewFilterControl = (filterKey) => {
    const mappedTab = CUSTOMER_FILTER_TAB_MAP[filterKey];
    if (!mappedTab) return true;
    return AuthService.canViewCustomerTab(mappedTab);
  };
  const visibleCustomerTypeFilter = canViewFilterControl('customerType');
  const visibleProgramTypeFilter = canViewFilterControl('programType');
  const visiblePaySourceFilter = canViewFilterControl('paySource');
  const visibleEyesOnFilter = canViewFilterControl('eyesOn');
  const visibleHasAirtagFilter = canViewFilterControl('hasAirtag');
  const visibleTransportationTypeFilter = canViewFilterControl('transportationType');
  const visibleLiftEquippedVehicleFilter = canViewFilterControl('liftEquippedVehicle');
  const hasVisibleFilters = visibleCustomerTypeFilter || visibleProgramTypeFilter || visiblePaySourceFilter
    || visibleEyesOnFilter || visibleHasAirtagFilter || visibleTransportationTypeFilter || visibleLiftEquippedVehicleFilter;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const site = EventsService.site;
  const [customers, setCustomers] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [transferMap, setTransferMap] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [sorting, setSorting] = useState({key: '', order: ''});
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showManageTableDropdown, setShowManageTableDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [programTypeFilter, setProgramTypeFilter] = useState('');
  const [paySourceFilter, setPaySourceFilter] = useState('');
  const [eyesOnFilter, setEyesOnFilter] = useState('');
  const [hasAirtagFilter, setHasAirtagFilter] = useState('');
  const [transportationTypeFilter, setTransportationTypeFilter] = useState('');
  const [liftEquippedVehicleFilter, setLiftEquippedVehicleFilter] = useState('');
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarModalCustomerId, setAvatarModalCustomerId] = useState(null);
  const [avatarCustomerName, setAvatarCustomerName] = useState('');
  const {
    imageUrl: avatarImageUrl,
    loading: avatarLoading,
    missing: avatarMissing,
  } = useCustomerAvatarUrl(avatarModalCustomerId, {
    enabled: showAvatarModal && Boolean(avatarModalCustomerId),
  });
  const isAllCustomersPage = title === 'All Customers';
  useEffect(() => {
    setSelectedItems([]);
  }, [showInactive]);
  const customerTabOrder = ['personalInfo', 'careServices', 'medicalInsurance', 'confidentialDetails', 'formSubmission'];
  const [columns, setColumns] = useState(getVisibleColumnsByPermission(CUSTOMER_COLUMNS));
  useEffect(() => {
    setColumns((prevColumns) => getVisibleColumnsByPermission(prevColumns));
  }, []);
  const listPageId = isAllCustomersPage ? "dashboard_customers_list_all" : "dashboard_customers_list";
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  useEffect(() => {
    const saved = readPageUiObject(listPageId);
    if (saved) {
      if (typeof saved.keyword === "string") setKeyword(saved.keyword);
      if (typeof saved.showInactive === "boolean") setShowInactive(saved.showInactive);
      if (saved.sorting && typeof saved.sorting === "object") setSorting(saved.sorting);
      if (typeof saved.customerTypeFilter === "string") setCustomerTypeFilter(saved.customerTypeFilter);
      if (typeof saved.programTypeFilter === "string") setProgramTypeFilter(saved.programTypeFilter);
      if (typeof saved.paySourceFilter === "string") setPaySourceFilter(saved.paySourceFilter);
      if (typeof saved.eyesOnFilter === "string") setEyesOnFilter(saved.eyesOnFilter);
      if (typeof saved.hasAirtagFilter === "string") setHasAirtagFilter(saved.hasAirtagFilter);
      if (typeof saved.transportationTypeFilter === "string") setTransportationTypeFilter(saved.transportationTypeFilter);
      if (typeof saved.liftEquippedVehicleFilter === "string") setLiftEquippedVehicleFilter(saved.liftEquippedVehicleFilter);
    }
    setFiltersHydrated(true);
  }, [listPageId]);

  const tableFiltersSnapshot = useMemo(
    () => ({
      keyword,
      showInactive,
      sorting,
      customerTypeFilter,
      programTypeFilter,
      paySourceFilter,
      eyesOnFilter,
      hasAirtagFilter,
      transportationTypeFilter,
      liftEquippedVehicleFilter,
    }),
    [keyword, showInactive, sorting, customerTypeFilter, programTypeFilter, paySourceFilter, eyesOnFilter, hasAirtagFilter, transportationTypeFilter, liftEquippedVehicleFilter]
  );
  useEffect(() => {
    if (!filtersHydrated) return;
    const t = setTimeout(() => {
      writePageUiObject(listPageId, tableFiltersSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [filtersHydrated, listPageId, tableFiltersSnapshot]);

  const toDisplayText = (value) => {
    if (value === undefined || value === null) return '';
    const normalized = `${value}`.trim();
    if (!normalized) return '';
    const lowered = normalized.toLowerCase();
    if (lowered === 'yes') return 'Yes';
    if (lowered === 'no') return 'No';
    return normalized;
  };

  const getOptionLabel = (options = [], value = '') => {
    const normalizedValue = `${value || ''}`.trim().toLowerCase();
    const option = options.find((item) => `${item?.value || ''}`.trim().toLowerCase() === normalizedValue);
    return option?.label || `${value || ''}`;
  };

  const formatAddress = (customer) => {
    const modernAddress = [
      customer?.address_line_1,
      customer?.address_line_2,
      customer?.city,
      customer?.state,
      customer?.zip_code
    ].filter(Boolean).join(', ');
    return modernAddress || customer?.address1 || customer?.address2 || customer?.address3 || customer?.address4 || customer?.address5 || '';
  };

  const formatEmergencyContact = (customer) => {
    const modernContact = [customer?.emergency_contact_name, customer?.emergency_contact_phone].filter(Boolean).join(' - ');
    return modernContact || customer?.emergency_contact || customer?.emergency_contact2 || '';
  };

  const formatCustomerRecord = (customer) => {
    const paySource = customer?.pay_source || '';
    const paySourceLabel = PAY_SOURCE_TEXT[paySource] || paySource;
    const paySourceDisplay = paySource === 'other' && customer?.pay_source_other
      ? `${paySourceLabel} (${customer.pay_source_other})`
      : paySourceLabel;

    const immigrationStatus = customer?.immigration_status || '';
    const immigrationStatusLabel = IMMIGRATION_STATUS_TEXT[immigrationStatus] || immigrationStatus;
    const immigrationStatusDisplay = immigrationStatus === 'other' && customer?.immigration_status_other
      ? `${immigrationStatusLabel} (${customer.immigration_status_other})`
      : immigrationStatusLabel;

    const referralSource = customer?.referral_source || '';
    const referralSourceLabel = REFERRAL_SOURCE_TEXT[referralSource] || referralSource;
    const referralSourceDisplay = referralSource === 'other' && customer?.referral_source_other
      ? `${referralSourceLabel} (${customer.referral_source_other})`
      : referralSourceLabel;

    const dischargeReason = customer?.discharge_reason || '';
    const dischargeReasonLabel = CUSTOMER_DISCHARGE_REASON_TEXT[dischargeReason] || dischargeReason;
    const dischargeReasonDisplay = dischargeReason === 'other' && customer?.discharge_reason_other
      ? `${dischargeReasonLabel} (${customer.discharge_reason_other})`
      : dischargeReasonLabel;

    return {
      ...customer,
      customer_name: formatCustomerDisplayName(customer),
      customer_type: CUSTOMER_TYPE_TEXT[customer?.type] || customer?.type || '',
      program_type_display: PROGRAM_TYPE_TEXT[customer?.program_type || customer?.program] || customer?.program_type || customer?.program || '',
      pay_source_display: paySourceDisplay,
      date_of_birth: customer?.birth_date || '',
      legal_sex_display: LEGAL_SEX_TEXT[customer?.legal_sex] || customer?.legal_sex || customer?.gender || '',
      marital_status_display: MARITAL_STATUS_TEXT[customer?.marital_status] || customer?.marital_status || '',
      marriage_date: customer?.marriage_date || '',
      immigration_status_display: immigrationStatusDisplay,
      language_spoken_display: Array.isArray(customer?.language_spoken) && customer.language_spoken.length > 0
        ? customer.language_spoken.map((lang) => getOptionLabel(LANGUAGE_OPTIONS, lang)).join(', ')
        : (customer?.language || ''),
      phone_number: customer?.phone || customer?.home_phone || customer?.mobile_phone || '',
      address_display: formatAddress(customer),
      emergency_contact_display: formatEmergencyContact(customer),
      admission_date: customer?.admission_date || '',
      days_of_week_display: Array.isArray(customer?.days_of_week) ? customer.days_of_week.map((day) => getOptionLabel(DAYS_OF_WEEK_OPTIONS, day)).join(', ') : '',
      enrolled_date: customer?.enrolled_date || '',
      created_by: customer?.create_by || '',
      referral_source_display: referralSourceDisplay,
      discharge_reason: dischargeReasonDisplay,
      notes_and_attachments: customer?.note || '',
      notes_for_driver: customer?.notes_for_driver || '',
      dietary_restrictions_display: Array.isArray(customer?.dietary_restrictions)
        ? customer.dietary_restrictions.map((item) => getOptionLabel(DIETARY_RESTRICTIONS_OPTIONS, item)).join(', ')
        : '',
      diet_texture_display: DIET_TEXTURE_TEXT[customer?.diet_texture] || customer?.diet_texture || '',
      table_number: customer?.table_id || '',
      seat_number: customer?.seat_number || '',
      transportation_type_display: TRANSPORTATION_TYPE_TEXT[customer?.transportation_type] || customer?.transportation_type || '',
      consent_to_text_messages_display: YES_NO_TEXT[`${customer?.consent_to_text_messages || ''}`.toLowerCase()] || toDisplayText(customer?.consent_to_text_messages || ''),
      preferred_text_language_display: PREFERRED_TEXT_LANGUAGE_TEXT[customer?.preferred_text_language] || customer?.preferred_text_language || '',
      consent_to_media_use_display: YES_NO_TEXT[`${customer?.consent_to_media_use || ''}`.toLowerCase()] || toDisplayText(customer?.consent_to_media_use || ''),
      primary_care_physician_display: customer?.primary_care_physician || customer?.care_provider || '',
      pharmacy_name: customer?.pharmacy || '',
      diabetes_mellitus_display: toDisplayText(customer?.diabetes_mellitus || ''),
      eyes_on_display: YES_NO_TEXT[`${customer?.eyes_on || (customer?.disability ? YES_NO.YES : '')}`.toLowerCase()] || toDisplayText(customer?.eyes_on || (customer?.disability ? YES_NO.YES : '')),
      wheelchair_display: YES_NO_TEXT[`${customer?.wheelchair || ''}`.toLowerCase()] || toDisplayText(customer?.wheelchair || ''),
      hospice_display: toDisplayText(customer?.hospice || ''),
      medications_given_at_center_display: YES_NO_TEXT[`${customer?.medications_given_at_center || ''}`.toLowerCase()] || toDisplayText(customer?.medications_given_at_center || ''),
      pillbox_display: YES_NO_TEXT[`${customer?.pillbox || ''}`.toLowerCase()] || toDisplayText(customer?.pillbox || ''),
      requires_rounding_display: YES_NO_TEXT[`${customer?.requires_rounding || ''}`.toLowerCase()] || toDisplayText(customer?.requires_rounding || '')
    };
  };


  useEffect(() => {
    if (!AuthService.canViewCustomers()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    setShowSpinner(true);
    CustomerService.getAllCustomers().then((data) => {
      setCustomers(
        data.data
          .map((item) => formatCustomerRecord(item))
          .sort(compareCustomersListDefaultOrder)
      );
    }).finally(() => {
      setShowSpinner(false);
    });
  }, []);

  useEffect(() => {
    let filtered = customers;
    
    // Basic keyword filter (name may be missing on some records)
    if (keyword) {
      const q = keyword.toLowerCase();
      filtered = filtered.filter((item) => {
        const hay = `${item?.name ?? ''} ${item?.customer_name ?? ''} ${item?.firstname ?? ''} ${item?.lastname ?? ''} ${item?.middle_name ?? ''} ${item?.name_cn ?? ''}`
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
        return hay.includes(q);
      });
    }
    
    // Active/Inactive filter (see isCustomerListActive in shared constants)
    if (showInactive) {
      filtered = filtered.filter((item) => !isCustomerListActive(item));
    } else {
      filtered = filtered.filter((item) => isCustomerListActive(item));
    }
    
    if (customerTypeFilter && visibleCustomerTypeFilter) {
      filtered = filtered.filter((item) => (item?.type || '') === customerTypeFilter);
    }

    if (programTypeFilter && visibleProgramTypeFilter) {
      filtered = filtered.filter((item) => (item?.program_type || item?.program || '') === programTypeFilter);
    }

    if (paySourceFilter && visiblePaySourceFilter) {
      const normalizePaySource = (v) =>
        `${v ?? ''}`.trim().toLowerCase().replace(/[\s_-]+/g, '');
      const filterNorm = normalizePaySource(paySourceFilter);
      const labelNorm = normalizePaySource(PAY_SOURCE_TEXT[paySourceFilter]);
      filtered = filtered.filter((item) => {
        const itemNorm = normalizePaySource(item?.pay_source);
        return itemNorm === filterNorm || (!!labelNorm && itemNorm === labelNorm);
      });
    }

    if (eyesOnFilter && visibleEyesOnFilter) {
      filtered = filtered.filter((item) => {
        const normalized = `${item?.eyes_on || (item?.disability ? YES_NO.YES : '')}`.toLowerCase();
        return normalized === eyesOnFilter;
      });
    }

    if (hasAirtagFilter && visibleHasAirtagFilter) {
      filtered = filtered.filter((item) => {
        const normalized = `${item?.has_airtag || ''}`.toLowerCase();
        return normalized === hasAirtagFilter;
      });
    }

    if (transportationTypeFilter && visibleTransportationTypeFilter) {
      filtered = filtered.filter((item) => (item?.transportation_type || '') === transportationTypeFilter);
    }

    if (liftEquippedVehicleFilter && visibleLiftEquippedVehicleFilter) {
      filtered = filtered.filter((item) => {
        const normalized = `${item?.wheelchair || ''}`.toLowerCase();
        return normalized === liftEquippedVehicleFilter;
      });
    }
    
    setFilteredCustomers(filtered);
  }, [keyword, customers, showInactive, customerTypeFilter, programTypeFilter, paySourceFilter, eyesOnFilter, hasAirtagFilter, transportationTypeFilter, liftEquippedVehicleFilter, visibleCustomerTypeFilter, visibleProgramTypeFilter, visiblePaySourceFilter, visibleEyesOnFilter, visibleHasAirtagFilter, visibleTransportationTypeFilter, visibleLiftEquippedVehicleFilter])

  useEffect(() => {
    if (sorting.key === "") {
      setCustomers((prev) => [...prev].sort(compareCustomersListDefaultOrder));
      return;
    }
    const sortedCustomers = [...customers].sort((a, b) => {
      if (sorting.key === "customer_name") {
        return compareCustomersByNameColumn(a, b);
      }
      if (CUSTOMER_DATE_SORT_COLUMN_KEYS.has(sorting.key)) {
        const ta = getCustomerListDateSortMs(a[sorting.key]);
        const tb = getCustomerListDateSortMs(b[sorting.key]);
        return ta - tb;
      }
      const av = a[sorting.key];
      const bv = b[sorting.key];
      return `${av ?? ""}`.localeCompare(`${bv ?? ""}`, undefined, { numeric: true, sensitivity: "base" });
    });
    setCustomers(
      sorting.order === "asc" ? sortedCustomers : [...sortedCustomers].reverse()
    );
  }, [sorting]);

  const getSortingImg = (key) => {
    return sorting.key === key ? (sorting.order === 'asc' ? 'up_arrow' : 'down_arrow') : 'default';
  }

  const sortTableWithField = (key) => {
    if (sorting.key === key) {
      setSorting({key: key, order: sorting.order === 'asc' ? 'desc' : 'asc'});
    } else {
      setSorting({key: key, order: 'asc'});
    }
  }

  const visibleCustomerIds = filteredCustomers.map((item) => item.id).filter(Boolean);
  const allVisibleSelected =
    visibleCustomerIds.length > 0 && visibleCustomerIds.every((id) => selectedItems.includes(id));
  const someVisibleSelected = visibleCustomerIds.some((id) => selectedItems.includes(id));

  const toggleSelectedAllItems = () => {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleCustomerIds);
      setSelectedItems((prev) => prev.filter((id) => !visibleSet.has(id)));
      return;
    }
    setSelectedItems((prev) => Array.from(new Set([...prev, ...visibleCustomerIds])));
  }

  const toggleItem  = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(item => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  }

  const checkSelectAll = () => allVisibleSelected;

  const selectedCustomerRecords = customers.filter((customer) => selectedItems.includes(customer.id));

  const cleanFilterAndClose = () => {
    setCustomerTypeFilter('');
    setProgramTypeFilter('');
    setPaySourceFilter('');
    setEyesOnFilter('');
    setHasAirtagFilter('');
    setTransportationTypeFilter('');
    setLiftEquippedVehicleFilter('');
    setShowFilterDropdown(false);
  }

  const FilterAndClose = () => {
    setShowFilterDropdown(false);
  }

  const getOptionsFromEnum = (enumMap, textMap) => {
    return Object.values(enumMap).map((value) => ({
      value,
      label: textMap?.[value] || value
    }));
  };

  const handleColumnsChange = (newColumns) => {
    const visibleNewColumns = getVisibleColumnsByPermission(newColumns);
    if (isAllCustomersPage) {
      const nextColumnByKey = new Map(visibleNewColumns.map((column) => [column.key, column]));
      setColumns((prevColumns) => prevColumns.map((column) => {
        const updatedColumn = nextColumnByKey.get(column.key);
        return updatedColumn ? { ...column, show: updatedColumn.show } : column;
      }));
      return;
    }
    setColumns(visibleNewColumns);
  }

  const goToEdit = (id) => {
    const editableTab = AuthService.canEditCustomerTab('personalInfo')
      ? 'personalInfo'
      : customerTabOrder.find((tabKey) => AuthService.canEditCustomerTab(tabKey));
    if (!editableTab) return;
    navigate(`/customers/edit/${id}?tab=${editableTab}&fromEdit=1`);
  }

  const goToCreateNew = () => {
    navigate(`/customers`);
  }

  const setTransferValue = (customerId, site) => {
    setTransferMap(prev => ({
      ...prev,
      [customerId]: site
    }));
  }

  const goToView = (id) => {
    navigate(`/customers/${id}`);
  }

  const showProfilePicture = (customer) => {
    setAvatarCustomerName(formatCustomerDisplayName(customer) || customer?.name || "");
    setAvatarModalCustomerId(customer?.id || null);
    setShowAvatarModal(true);
  }

  const closeAvatarModal = () => {
    setShowAvatarModal(false);
    setAvatarModalCustomerId(null);
    setAvatarCustomerName('');
  }

  const visibleTableColumns = columns.filter((column) => (
    column.show && (showInactive || !DISCHARGE_ONLY_COLUMN_KEYS.has(column.key))
  ));

  const table = <div className={`list row${freezeTableHeader && !showBreadcrumb ? '' : ' mb-4'}`}>
    <div
      className={`col-md-12${freezeTableHeader ? ' sticky-table-scroll' : ''}`}
      style={{
        overflow: 'auto',
        ...(freezeTableHeader
          ? {}
          : {
              minHeight: isAllCustomersPage ? 'calc(100vh - 260px)' : undefined,
              paddingBottom: isAllCustomersPage ? '24px' : undefined,
            }),
      }}
    >
      <table
        className={`personnel-info-table${freezeTableHeader ? ' personnel-info-table--freeze-leading' : ''}${enableBulkSelect && freezeTableHeader ? ' personnel-info-table--freeze-leading--with-checkbox' : ''}`}
      >
        <thead>
          <tr>
            {enableBulkSelect && (
              <th className="th-checkbox">
                <input
                  type="checkbox"
                  checked={checkSelectAll()}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                  }}
                  onChange={toggleSelectedAllItems}
                  aria-label="Select all customers"
                />
              </th>
            )}
            <th className="th-index">No.</th>
            {
              visibleTableColumns.map((column, index) => (
                <th
                  className={`sortable-header${column.key === 'customer_name' ? ' th-freeze-customer-name' : ''}`}
                  key={index}
                >
                  {column.label}{' '}
                  <span className="float-right" onClick={() => sortTableWithField(column.key)}>
                    <img src={`/images/${getSortingImg(column.key)}.png`} alt="" />
                  </span>
                </th>
              ))
            }
          </tr>
        </thead>
        <tbody>
          {
            filteredCustomers.map((customer, index) => <tr key={customer.id}>
              {enableBulkSelect && (
                <td className="td-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(customer.id)}
                    onChange={() => toggleItem(customer.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${customer?.customer_name || customer?.name || 'customer'}`}
                  />
                </td>
              )}
              <td className="td-index">{index + 1}</td>
              {visibleTableColumns.map((column) => (
                column.key === 'customer_name' ? (
                  <td key={column.key} className="td-freeze-customer-name" style={{ verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {AuthService.canViewCustomers() && (
                        <CustomerAvatar
                          customerId={customer.id}
                          alt={customer?.name}
                          size={64}
                          onClick={() => showProfilePicture(customer)}
                        />
                      )}
                      {AuthService.canEditAnyCustomerTab() && <PencilSquare size={16} className="clickable" onClick={() => goToEdit(customer?.id)} style={{ flexShrink: 0 }}></PencilSquare>}
                      <span className="clickable" style={{ color: '#0066B1', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => goToView(customer?.id)}>{customer?.customer_name}</span>
                    </div>
                  </td>
                ) : column.key === 'meal_type' ? (
                  <td key={column.key}>{MEAL_TYPE_TEXT[customer?.meal_type] || customer?.meal_type || ''}</td>
                ) : <td key={column.key}>{customer?.[column.key] || ''}</td>
              ))}
            </tr>)
          }
        </tbody>
      </table>
    </div>
  </div>;

  const customFilterMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <h6>Filter By</h6>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            {visibleCustomerTypeFilter && <div className="me-4">
              <div className="field-label">Customer Type</div>
              <select value={customerTypeFilter} onChange={(e) => setCustomerTypeFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(CUSTOMER_TYPE, CUSTOMER_TYPE_TEXT).filter((item) => !HIDDEN_CUSTOMER_TYPE_FILTER_VALUES.includes(item.value)).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
            {visibleProgramTypeFilter && <div className="me-4">
              <div className="field-label">Program Type</div>
              <select value={programTypeFilter} onChange={(e) => setProgramTypeFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(PROGRAM_TYPE, PROGRAM_TYPE_TEXT).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            {visiblePaySourceFilter && <div className="me-4">
              <div className="field-label">Pay Source</div>
              <select value={paySourceFilter} onChange={(e) => setPaySourceFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(PAY_SOURCE, PAY_SOURCE_TEXT).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            {visibleEyesOnFilter && <div className="me-4">
              <div className="field-label">Eyes-On</div>
              <select value={eyesOnFilter} onChange={(e) => setEyesOnFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(YES_NO, YES_NO_TEXT).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
            {visibleHasAirtagFilter && <div className="me-4">
              <div className="field-label">Has Airtag</div>
              <select value={hasAirtagFilter} onChange={(e) => setHasAirtagFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(YES_NO, YES_NO_TEXT).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            {visibleTransportationTypeFilter && <div className="me-4">
              <div className="field-label">Transportation Type</div>
              <select value={transportationTypeFilter} onChange={(e) => setTransportationTypeFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(TRANSPORTATION_TYPE, TRANSPORTATION_TYPE_TEXT).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
            {visibleLiftEquippedVehicleFilter && <div className="me-4">
              <div className="field-label">Lift-Equipped Vehicle Required</div>
              <select value={liftEquippedVehicleFilter} onChange={(e) => setLiftEquippedVehicleFilter(e.currentTarget.value)}>
                <option value="">All</option>
                {getOptionsFromEnum(YES_NO, YES_NO_TEXT).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>}
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => cleanFilterAndClose()}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={() => FilterAndClose()}> Filter </button>
            </div>
          </div>
        </div>
      );
    },
  );

  return (
    <>
      {showSpinner && <div className="spinner-overlay">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>}
      {showBreadcrumb && (
        <div className="list row mb-4">
          <Breadcrumb>
            <Breadcrumb.Item href="/">General</Breadcrumb.Item>
            <Breadcrumb.Item active>
              Customer Information
            </Breadcrumb.Item>
          </Breadcrumb>
          <div className="col-md-12 text-primary">
            <h4>{title || 'All Customers'}</h4>
          </div>
        </div>
      )}
      <div className={`app-main-content-list-container ${showBreadcrumb ? 'list-page' : ''} ${isAllCustomersPage ? 'all-customers-list-page' : ''}`}>
        <div className="app-main-content-list-func-container">
          {/*
            Do not reuse one {table} element as children of both Tab panes — a React element
            may only have one parent; duplicate references caused intermittent stale/wrong lists.
            Controlled activeKey keeps UI in sync with showInactive (including persisted filters).
          */}
          <Tabs
            id="customers-tab"
            activeKey={showInactive ? 'archivedCustomers' : 'activeCustomers'}
            onSelect={(k) => {
              if (k !== 'activeCustomers' && k !== 'archivedCustomers') return;
              setShowInactive(k === 'archivedCustomers');
            }}
          >
            <Tab eventKey="activeCustomers" title="Active Customers" />
            <Tab eventKey="archivedCustomers" title="Discharged Customers" />
          </Tabs>
          {enableBulkSelect && selectedItems.length > 0 && (
            <div className="bulk-edit-selection-summary">
              <strong>{selectedItems.length}</strong> customer{selectedItems.length === 1 ? "" : "s"} selected
              <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setSelectedItems([])}>
                Clear selection
              </button>
            </div>
          )}
          {table}
          <div className="list-func-panel">
            <input className="me-2 with-search-icon" type="text" placeholder="Search" value={keyword}  onChange={(e) => setKeyword(e.currentTarget.value)} />
            {hasVisibleFilters && <Dropdown
              key={'filter-customers'}
              id="filter-customers"
              className="me-2"
              show={showFilterDropdown}
              onToggle={(isOpen) => {
                if (isOpen) {
                  setShowManageTableDropdown(false);
                  setShowExportDropdown(false);
                }
                setShowFilterDropdown(isOpen);
              }}
              autoClose={false}
            > 
              <Dropdown.Toggle variant="outline-secondary">
                <Filter size={16} className="me-2"></Filter>Filter
              </Dropdown.Toggle>
              <Dropdown.Menu as={customFilterMenu}/> 
            </Dropdown>}
            <ManageTable 
              columns={showInactive ? columns : columns.filter((column) => !DISCHARGE_ONLY_COLUMN_KEYS.has(column.key))}
              onColumnsChange={handleColumnsChange}
              show={showManageTableDropdown}
              onToggle={(isOpen) => {
                if (isOpen) {
                  setShowFilterDropdown(false);
                  setShowExportDropdown(false);
                }
                setShowManageTableDropdown(isOpen);
              }}
            />
            {typeof additionalButtons === 'function' 
              ? additionalButtons({
                  showExportDropdown,
                  selectedCustomers: selectedCustomerRecords,
                  selectedCount: selectedItems.length,
                  onClearSelection: () => setSelectedItems([]),
                  exportColumns: visibleTableColumns.map((column) => {
                    const tab = CUSTOMER_LIST_COLUMN_TAB_MAP[column.key];
                    return tab ? { ...column, requiresCustomerTab: tab } : { ...column };
                  }),
                  exportData: filteredCustomers.map((customer) => {
                    const exportRecord = {};
                    visibleTableColumns.forEach((column) => {
                      exportRecord[column.key] = customer?.[column.key] || '';
                    });
                    return exportRecord;
                  }),
                  onExportToggle: (isOpen) => {
                    if (isOpen) {
                      setShowFilterDropdown(false);
                      setShowManageTableDropdown(false);
                    }
                    setShowExportDropdown(isOpen);
                  }
                })
              : additionalButtons
            }
          </div>
        </div>
      </div>
      <Modal show={showAvatarModal} onHide={closeAvatarModal} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>{avatarCustomerName}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          {avatarLoading && <Spinner animation="border" variant="primary" />}
          {!avatarLoading && avatarImageUrl && (
            <img 
              src={avatarImageUrl}
              alt={avatarCustomerName}
              style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
            />
          )}
          {!avatarLoading && avatarMissing && (
            <div style={{ textAlign: 'center', color: '#999' }}>No profile picture available</div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
};

export default DashboardCustomersList; 